import React, { forwardRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { motion, MotionProps } from 'motion/react';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { WidthKey, WIDTH_DIMS } from './constants';
import { PanelContext } from './context';
import { panelPriority, type PanelConfig } from './PANEL-CONFIG';
import { usePanelStore } from './store';

const createPanelVariants = (width: number | string) => ({
  initial: { width: 0, opacity: 0 },
  animate: { width: width, opacity: 1 },
});

const panelContainerStyles = (priority: number) => css`
  order: ${priority};
  display: flex;
  flex-direction: column;
  background-color: white;
  max-width: 100%;
  position: relative;
  height: 100%;
  overflow: hidden;

  will-change: width;
  border-left: 1px solid ${theme.colors.neutral20};
  border-top: 1px solid ${theme.colors.neutral20};
`;

const panelSectionStyles = (currentPanelWidth: number | string) => css`
  min-width: ${typeof currentPanelWidth === 'string' ? `${currentPanelWidth}` : `min(${currentPanelWidth}px, 100vw)`};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

export const closeTopBarPanelEvent = 'close-top-bar-panels';

const closeTopBarPanel = () => {
  const event = new CustomEvent(closeTopBarPanelEvent);
  window.dispatchEvent(event);
};

export type PanelPortalProps = React.ComponentPropsWithoutRef<'div'> & {
  id: PanelConfig;
  width?: WidthKey;
  children: React.ReactNode;
  onClose: () => void;
  enableAnimations?: boolean;
} & MotionProps;

export const PanelPortal = forwardRef<HTMLDivElement, PanelPortalProps>(
  ({ id, width = 'small', children, onClose, enableAnimations = false, ...rest }, ref) => {
    const currentWidth = usePanelStore((state) => state.currentPanelWidths[id]);
    const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
    const { registerPanel, unregisterPanel, trayIds, setLastInteractedWith, setPanelCloseFunction, updatePanelWidth } =
      usePanelStore();
    const priority = panelPriority[id];

    useLayoutEffect(() => {
      closeTopBarPanel();
      registerPanel(id, width, onClose, priority);
      return () => {
        unregisterPanel(id);
      };
    }, []);

    const isInTray = useMemo(() => trayIds.includes(id), [trayIds, id]);
    const fullscreenPanelId = usePanelStore((state) => state.fullscreenPanelId);
    const isFullscreen = useMemo(() => fullscreenPanelId === id, [fullscreenPanelId, id]);

    const trayRoot = useMemo(() => document.getElementById('tray-root'), []);
    const panelRoot = useMemo(() => document.getElementById('panel-root'), []);
    const fullscreenRoot = useMemo(() => document.getElementById('fullscreen-root'), []);
    const currentPanelWidth = useMemo(
      () => (isFullscreen ? '100%' : WIDTH_DIMS[currentWidth] ?? WIDTH_DIMS[width]),
      [currentWidth, width, isFullscreen]
    );

    const panelVariants = useMemo(
      () => createPanelVariants(isFullscreen || isXSmall ? '100%' : currentPanelWidth),
      [currentPanelWidth, isXSmall, isFullscreen]
    );

    const handleInteraction = useCallback(() => {
      setLastInteractedWith(id);
    }, [id]);

    useLayoutEffect(() => {
      setPanelCloseFunction(id, onClose);
    }, [onClose]);

    useLayoutEffect(() => {
      const currentWidth = usePanelStore.getState().currentPanelWidths?.[id];
      if (currentWidth !== width) {
        updatePanelWidth(id, width);
      }
    }, [width]);

    if (!trayRoot || !panelRoot || !fullscreenRoot) return null;

    const target = isFullscreen ? fullscreenRoot : isInTray ? trayRoot : panelRoot;

    return (
      <PanelContext.Provider value={{ id }}>
        {createPortal(
          <motion.div
            ref={ref}
            tabIndex={-1}
            variants={enableAnimations ? panelVariants : undefined}
            initial={enableAnimations ? 'initial' : undefined}
            animate={enableAnimations ? 'animate' : undefined}
            exit={enableAnimations ? 'initial' : undefined}
            transition={{ duration: 0.3, ease: 'linear' }}
            style={{ width: isFullscreen || isXSmall ? '100%' : currentPanelWidth }}
            css={panelContainerStyles(priority)}
            onFocusCapture={handleInteraction}
            onPointerDown={handleInteraction}
            {...rest}
          >
            <motion.section css={panelSectionStyles(isFullscreen || isXSmall ? '100%' : currentPanelWidth)}>
              {children}
            </motion.section>
          </motion.div>,
          target
        )}
      </PanelContext.Provider>
    );
  }
);

PanelPortal.displayName = 'PanelPortal';
